<div class="bp-shell" *ngIf="form" [ngStyle]="themeVars">

	<!-- ═══ Brand Summary ════════════════════════════════════════════════ -->
	<div class="bp-summary">
		<div class="bp-summary__main">
			<div class="bp-summary__avatar">
				<mat-icon>storefront</mat-icon>
			</div>
			<div class="bp-summary__text">
				<div class="bp-summary__name">{{ payoutRow?.suppname || vendor?.suppname }}</div>
				<div class="bp-summary__id">#{{ vendor?.supplierid }}</div>
			</div>
		</div>
		<div class="bp-summary__metric" *ngIf="brandMode">
			<div class="bp-summary__metric-label">{{ dashboardNetPayout < 0 ? 'Negative Balance' : 'Net Payout' }}</div>
			<div class="bp-summary__metric-amount">{{ dashboardNetPayout | currency }}</div>
		</div>
		<div class="bp-summary__metric" *ngIf="!brandMode">
			<div class="bp-summary__metric-label">{{ suggestedAmount ? 'Past Due' : 'Open AP' }}</div>
			<div class="bp-summary__metric-amount">{{ genericDueDisplay | currency }}</div>
		</div>
	</div>
	<div class="bp-reconcile-warn" *ngIf="dashboardNetPayout < 0">
		<mat-icon>error</mat-icon>
		<span>This brand is not payable right now. Current signed position is <strong>{{ dashboardNetPayout | currency }}</strong>.</span>
	</div>

	<!-- Quick stats (visible when payout data present) -->
	<div class="bp-stats" *ngIf="payoutRow">
		<!-- Payout math, in the same order as the dashboard card: Available - Hold = Payout. -->
		<div class="bp-stat">
			<span class="bp-stat__label">Available</span>
			<span class="bp-stat__value bp-stat__value--positive">{{ dashboardLinkReady | currency }}</span>
		</div>
		<div class="bp-stat">
			<span class="bp-stat__label">Less Payout Hold</span>
			<span class="bp-stat__value bp-stat__value--warn">{{ dashboardPayoutHold | currency }}</span>
		</div>
		<div class="bp-stat">
			<span class="bp-stat__label">Payable</span>
			<span class="bp-stat__value bp-stat__value--positive">{{ dashboardNetPayout | currency }}</span>
		</div>
		<!-- Reference only - open-AP basis, not deducted above. -->
		<div class="bp-stat">
			<span class="bp-stat__label">AP Open <span class="bp-stat__tag">ref</span></span>
			<span class="bp-stat__value">{{ toNum(payoutRow.brand_ap_open) | currency }}</span>
		</div>
		<div class="bp-stat">
			<span class="bp-stat__label">Excise &rarr; NYS <span class="bp-stat__tag">ref</span></span>
			<span class="bp-stat__value">{{ dashboardMetrcWithhold | currency }}</span>
		</div>
	</div>

	<form [formGroup]="form" class="bp-form">

		<!-- ═══ Payment Details ═══════════════════════════════════════════ -->
		<section class="bp-section">
			<div class="bp-section__header">
				<mat-icon class="bp-section__icon">payment</mat-icon>
				<span class="bp-section__title">Payment Details</span>
			</div>

			<!-- Payment method chips -->
			<div class="bp-method-row">
				<button type="button"
					*ngFor="let m of payment_methods"
					class="bp-method-chip"
					[class.bp-method-chip--active]="form.value.payment_type === m.paymentname"
					(click)="form.get('payment_type').setValue(m.paymentname)">
					{{ m.paymentname }}
				</button>
			</div>

			<div class="bp-fields">
				<mat-form-field appearance="outline" class="bp-field bp-field--date">
					<mat-label>Date</mat-label>
					<input matInput formControlName="datepaid" [matDatepicker]="dp">
					<mat-datepicker-toggle matSuffix [for]="dp"></mat-datepicker-toggle>
					<mat-datepicker #dp></mat-datepicker>
				</mat-form-field>

				<mat-form-field appearance="outline" class="bp-field bp-field--bank">
					<mat-label>Bank Account</mat-label>
					<mat-select formControlName="bankaccount">
						<mat-option *ngFor="let b of bankaccounts" [value]="b.accountcode">{{ b.bankaccountname }}</mat-option>
					</mat-select>
				</mat-form-field>

				<mat-form-field appearance="outline" class="bp-field bp-field--ref">
					<mat-label>Reference</mat-label>
					<input matInput formControlName="reference">
				</mat-form-field>

				<mat-form-field appearance="outline" class="bp-field bp-field--check" *ngIf="payingByCheck">
					<mat-label>Check #</mat-label>
					<input matInput formControlName="checkno" placeholder="next check number">
				</mat-form-field>

				<mat-form-field appearance="outline" class="bp-field bp-field--desc">
					<mat-label>Description</mat-label>
					<input matInput formControlName="description">
				</mat-form-field>
			</div>

			<div class="bp-amount-wrap">
				<label class="bp-amount-label">Payment Amount</label>
				<div class="bp-amount-input-wrap"
					[class.bp-amount-input-wrap--variance]="Math.abs(unallocated) >= 0.01">
					<span class="bp-amount-currency">$</span>
					<input class="bp-amount-input"
						type="number"
						step="0.01"
						min="0"
						formControlName="amount"
						appSelectOnFocus>
				</div>
				<!-- "Whole" pays whole invoices only, so the wire lands under the payable
				     whenever the leftover cash is smaller than the next invoice. Say so here,
				     at the box, instead of leaving the operator to reconcile this against the
				     dashboard's Final Payout on their own. -->
				<div class="bp-amount-note" *ngIf="dashboardNetPayout > 0 && rollsForward > 0.005">
					{{ dashboardNetPayout | currency }} payable &minus; {{ rollsForward | currency }} with no whole
					invoice to land on = this payment. The {{ rollsForward | currency }} stays payable and
					rolls to the next payout.
				</div>
			</div>
		</section>

		<!-- ═══ On-account credits (unapplied prior payments) — applied to invoices, no new cash ═══ -->
		<section class="bp-section bp-section--credits" *ngIf="!loadingInvoices && hasCredits">
			<div class="bp-section__header">
				<button type="button" class="bp-section__toggle" (click)="creditsOpen = !creditsOpen"
					[attr.aria-expanded]="creditsOpen" aria-label="Show or hide credits on account">
					<mat-icon>{{ creditsOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
				</button>
				<mat-icon class="bp-section__icon">account_balance_wallet</mat-icon>
				<span class="bp-section__title">Credits on Account — Apply to Invoices</span>
				<span class="bp-section__hint" *ngIf="creditsOpen">Prior payments not yet applied · untick any to skip</span>
				<span class="bp-section__hint" *ngIf="!creditsOpen">
					{{ selectedCredits.length }} of {{ availableCredits.length }} ticked · {{ creditAppliedTotal | currency }} applied
				</span>
			</div>

			<div class="bp-credits" *ngIf="creditsOpen">
				<div class="bp-credit"
					*ngFor="let cr of visibleCredits"
					[class.bp-credit--active]="cr.selected">
					<mat-checkbox class="bp-credit__check"
						[checked]="cr.selected"
						(change)="toggleCredit(cr, $event.checked)">
					</mat-checkbox>
					<div class="bp-credit__info">
						<div class="bp-credit__top">
							<span class="bp-credit__type">{{ cr.suppreference || cr.typename || 'Credit' }}</span>
							<span class="bp-credit__num">#{{ cr.transno }}</span>
						</div>
						<div class="bp-credit__bottom">
							<span class="bp-credit__date">{{ cr.trandate }}</span>
							<span class="bp-credit__ref" *ngIf="cr.transtext" [title]="cr.transtext"> · 📝</span>
						</div>
					</div>
					<div class="bp-credit__amounts">
						<span class="bp-credit__avail">{{ toNum(cr.leftto) | currency }} available</span>
						<span class="bp-credit__applied" *ngIf="creditAppliedForCredit(cr) > 0">
							−{{ creditAppliedForCredit(cr) | currency }} applied
						</span>
					</div>
				</div>
			</div>

			<div class="bp-render-more" *ngIf="creditsOpen && hiddenCreditCount">
				<span class="bp-render-more__text">
					Showing {{ visibleCredits.length }} of {{ availableCredits.length }} credits.
					All ticked credits are applied, including the ones not shown.
				</span>
				<span class="bp-render-more__actions">
					<button type="button" class="btn btn-sm btn-outline-primary" (click)="showMoreCredits()">
						Show more
					</button>
				</span>
			</div>
		</section>

		<!-- ═══ Apply to Invoices ════════════════════════════════════════ -->
		<section class="bp-section bp-section--invoices">
			<div class="bp-section__header">
				<button type="button" class="bp-section__toggle" (click)="invoicesOpen = !invoicesOpen"
					[attr.aria-expanded]="invoicesOpen" aria-label="Show or hide the invoice list">
					<mat-icon>{{ invoicesOpen ? 'expand_less' : 'expand_more' }}</mat-icon>
				</button>
				<mat-icon class="bp-section__icon">receipt_long</mat-icon>
				<span class="bp-section__title">Apply to Invoices</span>
				<span class="bp-section__hint" *ngIf="invoices.length">
					{{ selectedInvoices.length }} of {{ invoices.length }} selected<span *ngIf="preselectApplied"> — the documents you picked</span>
				</span>

				<div class="bp-section__actions">
					<button type="button" mat-button class="bp-mini-btn" (click)="selectAll()" [disabled]="loadingInvoices || !invoices.length">
						<mat-icon>done_all</mat-icon> All
					</button>
					<button type="button" mat-button class="bp-mini-btn" (click)="clearAll()" [disabled]="loadingInvoices || !invoices.length">
						<mat-icon>clear</mat-icon> None
					</button>
					<button type="button" mat-button class="bp-mini-btn" (click)="fillSelectedToAmount()" [disabled]="loadingInvoices || !invoices.length || paymentAmount <= 0" matTooltip="Roll oldest→newest, whole invoices only, and set the payment amount to what they total — the rest rolls forward to the next payout">
						<mat-icon>auto_fix_high</mat-icon> Whole
					</button>
					<button type="button" mat-button class="bp-mini-btn" (click)="fillWithPartials()" [disabled]="loadingInvoices || !invoices.length || paymentAmount <= 0" matTooltip="Roll oldest→newest and partial-pay the last invoice so the full amount is applied (nothing rolls forward)">
						<mat-icon>call_split</mat-icon> Partial
					</button>
				</div>
			</div>

			<!--
				Focused / All, and a quick search. Both are display only: the footer totals
				and what gets posted always cover every loaded document, ticked or not, on
				screen or not. A search runs over the whole list even while focused — that
				is how you go and find the invoice that isn't ticked yet.
			-->
			<div class="bp-listbar" *ngIf="invoicesOpen && !loadingInvoices && invoices.length > 1">
				<div class="bp-listbar__views" role="group" aria-label="Which invoices to show">
					<button type="button" class="bp-chip" [class.is-active]="pickedOnly && !searchingInvoices"
						[disabled]="searchingInvoices"
						(click)="setPickedOnly(true)">
						Picked {{ selectedInvoices.length }}
					</button>
					<button type="button" class="bp-chip" [class.is-active]="!pickedOnly && !searchingInvoices"
						[disabled]="searchingInvoices"
						(click)="setPickedOnly(false)">
						All {{ invoices.length }}
					</button>
				</div>

				<span class="bp-listbar__spacer"></span>

				<mat-form-field appearance="outline" class="bp-listbar__search">
					<mat-label>Find a document</mat-label>
					<input matInput type="search" autocomplete="off" [formControl]="invoiceSearch"
						placeholder="doc #, reference, amount…">
					<mat-icon matSuffix *ngIf="!searchingInvoices">search</mat-icon>
					<button type="button" mat-icon-button matSuffix *ngIf="searchingInvoices"
						(click)="clearInvoiceSearch()" aria-label="Clear search">
						<mat-icon>close</mat-icon>
					</button>
				</mat-form-field>
			</div>

			<div class="bp-listbar__note" *ngIf="invoicesOpen && !loadingInvoices && searchingInvoices">
				Searching all {{ invoices.length }} documents — {{ listedInvoices.length }} match
				&ldquo;{{ invoiceSearchTerm }}&rdquo;. Ticks you make here stick when you clear the search.
			</div>

			<!-- Loading state -->
			<div class="bp-loading" *ngIf="loadingInvoices">
				<svg class="spinner" width="26px" height="26px" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
					<circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
				</svg>
				<span>Loading invoices…</span>
			</div>

			<!-- Invoice list -->
			<div class="bp-invoices" *ngIf="invoicesOpen && !loadingInvoices && invoices.length">
				<div class="bp-invoice"
					*ngFor="let inv of visibleInvoices"
					[class.bp-invoice--selected]="inv.selected"
					[class.bp-invoice--partial]="isPartial(inv)">

					<mat-checkbox class="bp-invoice__check"
						[checked]="inv.selected"
						(change)="toggleInvoice(inv, $event.checked)"
						(click)="$event.stopPropagation()">
					</mat-checkbox>

					<div class="bp-invoice__info">
						<div class="bp-invoice__brand">
							{{ inv.brand_name || vendor?.suppname }} · #{{ inv.brand_id || vendor?.supplierid }}
						</div>
						<div class="bp-invoice__top">
							<span class="bp-invoice__type">{{ inv.typename }}</span>
							<span class="bp-invoice__num">#{{ inv.transno }}</span>
							<span class="bp-invoice__ready-badge" *ngIf="inv.customer_paid" matTooltip="Customer paid {{ inv.customer_invoice_gross | currency }} on their invoice; {{ inv.customer_paid_amount | currency }} is the brand portion we owe">
								<mat-icon>check_circle</mat-icon> Customer Paid
							</span>
							<span class="bp-invoice__pin-badge bp-invoice__pin-badge--partial" *ngIf="isPartial(inv)">
								PARTIAL · {{ toNum(inv.leftto) - toNum(inv.allocation) | currency }} left
							</span>
						</div>
						<div class="bp-invoice__bottom">
							<span class="bp-invoice__date">{{ inv.trandate }}</span>
							<span class="bp-invoice__ref" *ngIf="inv.suppreference"> · {{ inv.suppreference }}</span>
							<span class="bp-invoice__dispensary" *ngIf="inv.dispensary"> · {{ inv.dispensary }}</span>
							<span class="bp-invoice__paid-info" *ngIf="inv.customer_paid && inv.customer_invoice_gross > inv.brand_portion + 0.01">
								· Cust inv {{ inv.customer_invoice_gross | currency }}
							</span>
							<span class="bp-invoice__paid-info" *ngIf="inv.customer_paid">
								· Brand due {{ inv.customer_paid_amount | currency }}
							</span>
							<span class="bp-invoice__paid-info bp-invoice__paid-info--applied" *ngIf="paidAlready(inv) > 0.01"
								matTooltip="Already paid out on a prior run — only the Brand AP amount is still owed">
								· {{ paidAlready(inv) | currency }} already paid · {{ toNum(inv.leftto) | currency }} still open
							</span>
							<span class="bp-invoice__paid-info bp-invoice__paid-info--credit" *ngIf="toNum(inv.creditAlloc) > 0.005"
								matTooltip="Covered by an on-account credit — no new cash needed for this portion">
								· {{ toNum(inv.creditAlloc) | currency }} from credit
							</span>
						</div>
					</div>

					<div class="bp-invoice__due">
						<span class="bp-invoice__due-label">{{ brandMode ? 'Brand AP' : 'Open' }}</span>
						<span class="bp-invoice__due-value">{{ toNum(inv.leftto) | currency }}</span>
					</div>

					<div class="bp-invoice__alloc">
						<label class="bp-invoice__alloc-label">Pay</label>
						<input class="bp-invoice__alloc-input"
							type="number"
							step="0.01"
							min="0"
							[max]="toNum(inv.leftto) - toNum(inv.creditAlloc)"
							[value]="inv.allocation || ''"
							placeholder="0.00"
							(change)="updateAllocation(inv, $any($event.target).value)"
							(click)="$event.stopPropagation()"
							appSelectOnFocus>
					</div>

					<div class="bp-invoice__left" *ngIf="inv.selected">
						<label class="bp-invoice__left-label">Left</label>
						<span class="bp-invoice__left-value">{{ rollingLeftAfter(inv) | currency }}</span>
					</div>
				</div>
			</div>

			<!-- Render window notice. Selection and every footer total already cover ALL
			     open documents — only the painted rows are limited, so say so plainly. -->
			<!-- Nothing matched the search, or the focused view has nothing in it yet. -->
			<div class="bp-empty" *ngIf="invoicesOpen && !loadingInvoices && invoices.length && !listedInvoices.length">
				<mat-icon>search_off</mat-icon>
				<span *ngIf="searchingInvoices">No document matches &ldquo;{{ invoiceSearchTerm }}&rdquo;.</span>
				<span *ngIf="!searchingInvoices">Nothing picked yet.</span>
				<button type="button" mat-button class="bp-mini-btn" *ngIf="searchingInvoices" (click)="clearInvoiceSearch()">
					<mat-icon>clear</mat-icon> Clear search
				</button>
				<button type="button" mat-button class="bp-mini-btn" *ngIf="!searchingInvoices" (click)="setPickedOnly(false)">
					<mat-icon>list</mat-icon> Show all {{ invoices.length }}
				</button>
			</div>

			<div class="bp-render-more" *ngIf="invoicesOpen && hiddenInvoiceCount">
				<span class="bp-render-more__text">
					Showing {{ visibleInvoices.length }} of {{ listedInvoices.length }}{{ pickedOnly && !searchingInvoices ? ' picked' : '' }} documents.
					Selection and the totals below cover all {{ invoices.length }}.
				</span>
				<span class="bp-render-more__actions">
					<button type="button" class="btn btn-sm btn-outline-primary" (click)="showMoreInvoices()">
						Show {{ hiddenInvoiceCount < RENDER_PAGE ? hiddenInvoiceCount : RENDER_PAGE }} more
					</button>
					<button type="button" class="btn btn-sm btn-link" (click)="showAllInvoices()">
						Show all {{ listedInvoices.length }}
					</button>
				</span>
			</div>

			<!-- Empty -->
			<div class="bp-empty" *ngIf="!loadingInvoices && !invoices.length">
				<mat-icon>check_circle</mat-icon>
				<span>No outstanding invoices</span>
			</div>

			<div class="bp-reconcile-warn bp-reconcile-warn--info" *ngIf="!loadingInvoices && unallocated > 0.005">
				<mat-icon>info</mat-icon>
				<span>
					<strong>{{ unallocated | currency }}</strong> will sit as a credit on {{ payoutRow?.suppname || vendor?.suppname }}.
				</span>
				<button type="button" mat-button class="bp-warn-btn" (click)="snapWireToSelection()" matTooltip="Set amount = {{ cashAllocationTotal | currency }}">
					Snap to {{ cashAllocationTotal | currency }}
				</button>
			</div>
			<div class="bp-reconcile-warn" *ngIf="!loadingInvoices && unallocated < -0.005">
				<mat-icon>error</mat-icon>
				<span>
					Allocations exceed your payment by <strong>{{ -unallocated | currency }}</strong>.
				</span>
				<button type="button" mat-button class="bp-warn-btn" (click)="snapWireToSelection()" matTooltip="Set amount = {{ cashAllocationTotal | currency }}">
					Snap to {{ cashAllocationTotal | currency }}
				</button>
			</div>
			<div class="bp-reconcile-warn" *ngIf="!loadingInvoices && missingPreselect > 0">
				<mat-icon>warning</mat-icon>
				<span>
					<strong>{{ missingPreselect }}</strong>
					of the documents you picked {{ missingPreselect === 1 ? 'is' : 'are' }} not payable on this
					vendor's account any more (settled, on hold, or already paid) and
					{{ missingPreselect === 1 ? 'was' : 'were' }} left off.
				</span>
			</div>
			<div class="bp-reconcile-warn" *ngIf="!loadingInvoices && hasOverAllocation">
				<mat-icon>error</mat-icon>
				<span>One or more lines allocate more than the invoice balance. Fix before posting.</span>
			</div>
			<div class="bp-reconcile-warn bp-reconcile-warn--info" *ngIf="!loadingInvoices && rollsForward > 0.005 && dashboardNetPayout > 0 && Math.abs(unallocated) < 0.01">
				<mat-icon>schedule</mat-icon>
				<span>
					<strong>{{ rollsForward | currency }}</strong> rolls forward to the next payment.
				</span>
			</div>
			<div class="bp-reconcile-warn bp-reconcile-warn--info" *ngIf="!loadingInvoices && overpaysBrand > 0.005 && dashboardNetPayout > 0">
				<mat-icon>info</mat-icon>
				<span>
					Payment exceeds dashboard net payout by <strong>{{ overpaysBrand | currency }}</strong>. The excess will sit on {{ payoutRow?.suppname || vendor?.suppname }} as an on-account credit to apply next time.
				</span>
			</div>
		</section>

		<!-- ═══ Sticky footer ════════════════════════════════════════════ -->
		<div class="bp-footer">
			<div class="bp-footer__totals">
				<div class="bp-footer__row" *ngIf="dashboardNetPayout > 0">
					<span class="bp-footer__label">Payable (brand):</span>
					<span class="bp-footer__value">{{ dashboardNetPayout | currency }}</span>
				</div>
				<!-- Sits ABOVE the payment line and signed, so payable − deferred = this
				     payment reads straight down the column. Was labelled "Remaining paid",
				     which named it as money already sent — it is the opposite. -->
				<div class="bp-footer__row" *ngIf="rollsForward > 0.005">
					<span class="bp-footer__label">Rolls to next payout:</span>
					<span class="bp-footer__value">&minus;{{ rollsForward | currency }}</span>
				</div>
				<div class="bp-footer__row bp-footer__row--unalloc">
					<span class="bp-footer__label">This payment:</span>
					<span class="bp-footer__value">{{ paymentAmount | currency }}</span>
				</div>
				<div class="bp-footer__row" *ngIf="creditAppliedTotal > 0">
					<span class="bp-footer__label">From credits (no cash):</span>
					<span class="bp-footer__value">{{ creditAppliedTotal | currency }}</span>
				</div>
				<div class="bp-footer__row">
					<span class="bp-footer__label">To invoices:</span>
					<span class="bp-footer__value">{{ selectedTotal | currency }}</span>
				</div>
			</div>

			<div class="bp-footer__actions">
				<button type="button" mat-button class="bp-cancel-btn" (click)="cancel()" [disabled]="posting">
					Cancel
				</button>
				<button type="button" mat-stroked-button class="bp-preview-btn"
					*ngIf="payingByCheck"
					(click)="previewCheck()"
					[disabled]="posting || previewingCheck || paymentAmount <= 0"
					matTooltip="Render this check as a PDF without posting — check alignment before you commit">
					<mat-icon>print</mat-icon>
					{{ previewingCheck ? 'Building…' : 'Preview Check' }}
				</button>
				<button type="button" class="bp-pay-btn" (click)="post()" [disabled]="!canPost">
					<svg *ngIf="posting" class="spinner spinner--inline" width="18px" height="18px" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
						<circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
					</svg>
					<mat-icon *ngIf="!posting">account_balance</mat-icon>
					<!-- No new cash and credit landing = a pure application. The server writes
					     no supptrans/GL/bank row for a $0 payment, so don't call it one. -->
					<span *ngIf="!posting && creditOnlyRound">Apply Credit</span>
					<span *ngIf="!posting && !creditOnlyRound">Pay &amp; Apply</span>
					<span *ngIf="posting">Posting…</span>
				</button>
			</div>
		</div>

	</form>
</div>
